import type { PlaybackSource } from "../state/player";

export interface LiveChipInput {
  /** The view is pinned to the live edge. */
  atLive: boolean;
  /** What the player is pointed at, or null when nothing has been played. */
  sourceKind: PlaybackSource["kind"] | null;
  playing: boolean;
}

/** `state` reports where you already are. `action` is the same pill offering to take you there. */
export type LiveChip = "state" | "action";

/**
 * What the toolbar's live pill is: a statement, or a control.
 *
 * Pulled out of the JSX because the condition carries three separate claims and the page used
 * to disagree with itself about them: the player panel offered "Listen live" in exactly the
 * state where this said LIVE. One function, one answer, and the case that is easiest to get
 * wrong is the one that is tested.
 *
 * The pill says LIVE either way -- it is red when it is a statement and grey when it is an
 * offer, so the colour carries the difference and the word never moves. There is no separate
 * "GO LIVE" button: two labels in one slot read as two controls.
 *
 * Live means the view is at the edge *and* nothing else is claiming the audio. Pausing the live
 * stream is not a way out of it: the pause drops the source (`onPause` in useAudioSession), so
 * a paused
 * page reads exactly like one that has just loaded. Wheeling to the edge with an archive show
 * still playing is a way out, and there the pill is what actually switches the audio over.
 * The `playing` term is what is left holding a live source that never started -- an autoplay
 * refusal -- to the offer it is rather than a claim to be listening.
 */
export function liveChip({ atLive, sourceKind, playing }: LiveChipInput): LiveChip {
  if (atLive && (sourceKind === null || (sourceKind === "live" && playing))) return "state";
  return "action";
}
